import { useInfiniteQuery } from 'react-query';
import { Axios } from '@utils/api/axios-instance.ts';
import { GET_URL } from '@utils/api/endpoints.ts';
import { QueryKeys } from '@utils/api/query-keys.ts';
import type { PublishTargetsResponse } from '@utils/types/publishing/publish.ts';

interface Args {
  connectionId: string | null;
  /** Free-text filter — backend decides how to interpret (title / meeting name). */
  query?: string;
}

async function fetchPublishTargets(
  connectionId: string,
  query?: string,
  cursor?: string
): Promise<PublishTargetsResponse> {
  const url = GET_URL(connectionId).publishTargets;
  const params: Record<string, string> = {};
  if (query) params.q = query;
  if (cursor) params.cursor = cursor;
  const { data } = await Axios.get<PublishTargetsResponse>(url, {
    params: Object.keys(params).length > 0 ? params : undefined
  });
  return data;
}

/**
 * Infinite query so the dialog can "Load more" its way through a long
 * YouTube video list or iBabs meeting list. The caller is expected to
 * flatten pages with `data.pages.flatMap(p => p.result)` before passing
 * to the picker.
 */
export function useGetPublishTargets({ connectionId, query }: Args) {
  return useInfiniteQuery<PublishTargetsResponse>(
    QueryKeys.publishTargets(connectionId ?? '__none__', query),
    ({ pageParam }) =>
      fetchPublishTargets(
        connectionId as string,
        query,
        pageParam as string | undefined
      ),
    {
      enabled: !!connectionId,
      staleTime: 1000 * 30,
      getNextPageParam: last => last.next_cursor || undefined,
      keepPreviousData: true
    }
  );
}
